import { 
  collection, 
  doc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from './config';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';

// Helper function to check if Firebase is available
const checkFirebase = () => {
  if (!isFirebaseConfigured() || !db) {
    return { success: false, error: 'Firebase not configured' };
  }
  return { success: true };
};

// Helper function to handle Firebase errors and session timeouts
const handleFirebaseError = async (error: any): Promise<{ success: false; error: string; isSessionError: boolean }> => {
  console.error('Firebase error:', error);
  
  // Use the global error handler to check for auth errors and attempt token refresh
  const isAuthError = await handleFirebaseAuthError(error);
  
  // Check for authentication/session errors
  const isSessionError = 
    error.code === 'permission-denied' ||
    error.code === 'unauthenticated' ||
    error.message?.includes('permission') ||
    error.message?.includes('session') ||
    error.message?.includes('expired');
  
  if (isSessionError && !isAuthError) {
    // If it's a session error but token refresh didn't work, trigger logout
    console.log('⚠️ Session/auth error detected, user may need to re-authenticate');
    window.dispatchEvent(new CustomEvent('session_timeout'));
  }
  
  return {
    success: false,
    error: error.message || 'Firebase operation failed',
    isSessionError
  };
};

// User-related database operations
export const userService = {
  // Create a new user document
  createUser: async (userId: string, userData: any) => {
    console.log('🔍 createUser called with:', { userId, userData });
    
    const firebaseCheck = checkFirebase();
    if (!firebaseCheck.success) {
      console.log('❌ Firebase check failed:', firebaseCheck);
      return firebaseCheck;
    }

    try {
      console.log('📝 Creating user document...');
      const userRef = doc(db, 'users', userId);
      console.log('📝 User ref created:', userRef);
      
      // Ensure role is set (default to 'admin' for new signups if not provided)
      const userDocData = {
        ...userData,
        role: userData.role || 'admin', // Ensure role is always set, default to 'admin'
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      console.log('📝 User doc data:', userDocData);
      console.log('📝 User role in doc data:', userDocData.role);
      
      // Add timeout wrapper
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Operation timeout after 8 seconds')), 8000)
      );
      
      // Use merge: false for new users to ensure role is set, or merge: true if updating
      // But explicitly set role to ensure it's always present
      const setDocPromise = setDoc(userRef, userDocData, { merge: true });
      // After setting, verify role is in document (we'll do a quick check)
      
      await Promise.race([setDocPromise, timeoutPromise]);
      console.log('✅ User document created successfully');
      
      // Verify role was saved by reading it back
      const verifyDoc = await getDoc(userRef);
      if (verifyDoc.exists()) {
        const savedData = verifyDoc.data();
        const savedRole = savedData.role || 'NOT SET';
        console.log(`✅ Verified role in Firestore: ${savedRole}`);
        if (!savedData.role) {
          console.warn('⚠️ WARNING: Role was not saved! Attempting to fix...');
          // Try to set role again explicitly
          await setDoc(userRef, { role: 'admin' }, { merge: true });
          console.log('✅ Fixed: Role set to admin');
        }
      }
      
      return { success: true, id: userId };
    } catch (error: any) {
      return await handleFirebaseError(error);
    }
  },

  // Get user data
  getUser: async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        return { success: true, data: userSnap.data() };
      } else {
        return { success: false, error: 'User not found' };
      }
    } catch (error) {
      console.error('Error getting user:', error);
      return { success: false, error };
    }
  },

  // Update user data
  updateUser: async (userId: string, userData: any) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...userData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error };
    }
  },

  // Remove user from a specific organization (multi-organization support)
  removeUserFromOrganization: async (userId: string, organizationId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return { success: false, error: 'User not found' };
      }

      const userData = userSnap.data();
      const organizationIds = userData.organizationIds || [];
      
      // Backward compatibility: if user has single organizationId, add it to array
      let allOrgIds = [...organizationIds];
      if (userData.organizationId && !allOrgIds.includes(userData.organizationId)) {
        allOrgIds.push(userData.organizationId);
      }

      // Remove the organization from the array
      const updatedOrgIds = allOrgIds.filter(id => id !== organizationId);

      if (updatedOrgIds.length === allOrgIds.length) {
        return { success: false, error: 'User is not a member of this organization' };
      }

      // Prepare update data
      const updateData: any = {
        organizationIds: updatedOrgIds,
        updatedAt: serverTimestamp()
      };

      // If the removed organization was the current organizationId, update it
      if (userData.organizationId === organizationId) {
        // Set to first remaining organization, or null if none
        updateData.organizationId = updatedOrgIds.length > 0 ? updatedOrgIds[0] : null;
      }

      await updateDoc(userRef, updateData);
      
      console.log(`✅ Removed user ${userId} from organization ${organizationId}`);
      return { success: true };
    } catch (error: any) {
      console.error('Error removing user from organization:', error);
      return { success: false, error: error.message || 'Failed to remove user from organization' };
    }
  }
};

// Tasks-related database operations
export const taskService = {
  // Create a new task
  createTask: async (userId: string, taskData: any) => {
    try {
      const tasksRef = collection(db, 'tasks');
      const docRef = await addDoc(tasksRef, {
        ...taskData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating task:', error);
      return { success: false, error };
    }
  },

  // Get all tasks for a user
  getTasks: async (userId: string) => {
    try {
      const tasksRef = collection(db, 'tasks');
      const q = query(
        tasksRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const tasks: any[] = [];
      querySnapshot.forEach((doc) => {
        tasks.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, data: tasks };
    } catch (error) {
      console.error('Error getting tasks:', error);
      return { success: false, error };
    }
  },

  // Update a task
  updateTask: async (taskId: string, taskData: any) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        ...taskData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating task:', error);
      return { success: false, error };
    }
  },

  // Delete a task
  deleteTask: async (taskId: string) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await deleteDoc(taskRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting task:', error);
      return { success: false, error };
    }
  },

  // Real-time listener for tasks
  subscribeToTasks: (userId: string, callback: (tasks: any[]) => void) => {
    const tasksRef = collection(db, 'tasks');
    const q = query(
      tasksRef, 
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const tasks: any[] = [];
      querySnapshot.forEach((doc) => {
        tasks.push({ id: doc.id, ...doc.data() });
      });
      callback(tasks);
    });
  }
};
